import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useWebSocket from 'common/hooks/useWebSocket';

/**
 * Realtime progress for a queued bulk override run — one or several batches.
 *
 * Above `BULK_OVERRIDE_ASYNC_THRESHOLD` (50) smartphos-core chunks the work onto SQS and the
 * mutation returns `{ batchId, status: 'QUEUED', accepted }` with an EMPTY `results`. The outcome
 * then arrives over websocket: `ndc_override_queued`, then one `ndc_override_progress` per chunk,
 * then a single `ndc_override_completed` — all scoped by batchId.
 *
 * A single commit can now be SEVERAL batches: the client splits any run that would exceed the
 * platform's 100kb request-body limit into sequential mutations, each of which is its own batch
 * server-side. This hook tracks the set and presents them as one run — counts, chunk progress and
 * the settle are all aggregates, and the run is only complete when EVERY batch has delivered its
 * authoritative `ndc_override_completed`.
 *
 * The tracking API is therefore incremental: `beginRun` before the first submit, `addBatch` as
 * each mutation resolves, `finishRun` once the last has. Core publishes a batch's queued event
 * BEFORE that batch's own mutation returns, and an early batch's chunks are being consumed while
 * later chunks are still on the wire — so registration can never outrun the events. Two things
 * absorb that: batches register as they are accepted rather than at the end, and events for a
 * batch the active run does not know YET are buffered and drained on registration instead of
 * being dropped (or worse, mis-read as another run finishing in the background).
 * `startTracking` composes the three for the single-request commit.
 *
 * Three things make this different from useLabelRegenerationProgress, which it otherwise mirrors:
 *
 * - Progress is per CHUNK, not per line item. There is no cap on batch size, so per-item events
 *   would be thousands of EventBridge calls for one run. That means the bar advances in steps of
 *   the chunk size, and the counts come from the event rather than from matching ids.
 *
 * - Scoping is by `batchId`, not by line item ids. Core generates it server-side precisely so two
 *   runs cannot collide, and the mutation hands it back — so we can match exactly, with no
 *   guessing about which run an event belongs to.
 *
 * - Nothing is persisted server-side. There is no row tag to fall back on, so a refresh loses the
 *   progress bar by design and the timeout below is the ONLY backstop for a run whose chunks end
 *   up in the DLQ. It is not defensive polish; without it a stalled run leaves the dialog open
 *   forever. Because a completion event stopped being terminal the moment there could be more
 *   than one, every non-final event RE-ARMS that timeout rather than clearing it — clearing
 *   without re-arming would remove the last backstop and hang the dialog past every other guard.
 */

/**
 * A queued run is not bounded by the request. Chunks queue behind each other and behind other SQS
 * traffic on the same function, so this is generous — if it expires the caller reports what it
 * heard about and closes. Re-armed in full by every progress and non-final completion event, so
 * it measures silence, not total duration.
 */
const QUEUED_TIMEOUT_MS = 900000;

/**
 * How long to keep waiting for the remaining `ndc_override_completed` events once every chunk of
 * every batch has reported.
 *
 * The server is the authority on the outcome: it counts failures per batch in Redis and only
 * the last chunk to finish knows that batch's total. Settling on our own chunk count would race
 * those events and could announce the run twice, so the count arms this grace instead and the
 * final completion or its expiry settles the run exactly once.
 */
const COMPLETION_GRACE_MS = 30000;

/**
 * Background completions are aggregated before they reach the caller: an operator who moved
 * 1200 orders must be told "1200", once — not "400 updated" three times as each batch lands.
 *
 * Two layers, because there are two ways to stop tracking a run:
 *
 * - The dialog CLOSED but the page lives on (a timeout closed it, the operator closed it, the
 *   screen unmounted). The run's batch membership is remembered at MODULE level — it must
 *   outlive the hook instance — so each late completion is filed under its run and ONE
 *   notification fires when the whole run has reported, or after a quiet minute when a batch
 *   never does. Batches can finish many minutes apart behind a stalled queue, so no debounce
 *   window can group them; only membership can.
 *
 * - The PAGE was refreshed: the registry died with it, so completions from before the refresh
 *   fall back to the short debounce below. Batches finishing further apart than that still
 *   produce separate notifications — the acceptable degradation, since nothing in this tab
 *   remembers the run any more.
 */
const BACKGROUND_AGGREGATE_MS = 2500;

/** How long a remembered run waits for a straggler batch before reporting what it has. */
const BACKGROUND_RUN_QUIET_MS = 60000;

/* Bounded: an evicted run's late completions degrade to the debounce path; they are not lost. */
const MAX_REMEMBERED_RUNS = 8;

const rememberedRuns = [];
const runsByBatchId = new Map();

const rememberRun = () => {
  const run = {
    batchIds: new Set(),
    received: new Set(),
    totals: { total: 0, succeeded: 0, failed: 0 },
    isFinalized: false,
    isSettled: false,
    timer: null,
  };

  rememberedRuns.push(run);

  while (rememberedRuns.length > MAX_REMEMBERED_RUNS) {
    const evicted = rememberedRuns.shift();

    if (evicted.timer) clearTimeout(evicted.timer);
    evicted.batchIds.forEach((batchId) => runsByBatchId.delete(batchId));
  }

  return run;
};

/** Test seam: module state would otherwise leak run membership between test cases. */
export const resetBackgroundRunMemory = () => {
  while (rememberedRuns.length > 0) {
    const run = rememberedRuns.pop();

    if (run.timer) clearTimeout(run.timer);
  }

  runsByBatchId.clear();
};

/* The early-event buffer is bounded so a stream of foreign batchIds (another tab's runs, hour
 * after hour) cannot grow it: past these caps the oldest thing an active run has not claimed is
 * simply dropped, which is exactly what happened to ALL unclaimed events before the buffer. */
const MAX_BUFFERED_BATCHES = 32;
const MAX_BUFFERED_EVENTS_PER_BATCH = 256;

const EMPTY_SYNC = { succeeded: 0, failed: 0, total: 0 };

const useNdcOverrideProgress = ({
  queuedTimeoutMs = QUEUED_TIMEOUT_MS,
  /**
   * Called when a queued run finishes while nothing is being tracked — the user refreshed or
   * navigated away after submitting. There is no dialog left to update, so the caller decides how
   * to surface it. Completions arriving within a short quiet gap are summed into ONE call.
   */
  onBackgroundComplete,
} = {}) => {
  const { socket, isConnected } = useWebSocket();

  const [isTracking, setIsTracking] = useState(false);
  const [total, setTotal] = useState(0);
  const [succeeded, setSucceeded] = useState(0);
  const [failed, setFailed] = useState(0);
  const [doneBatches, setDoneBatches] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [percent, setPercent] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  // Refs, not state, for everything the socket handler reads: the handler is registered once per
  // socket and must not be torn down and rebuilt on every arriving event.
  //
  // batchesRef: batchId -> { accepted, chunksSeen:Set, succeeded, failed, totalChunks, final } —
  // one entry per registered batch of the run, `final` holding the batch's authoritative
  // completed counts once its ndc_override_completed lands (null until then).
  const batchesRef = useRef(new Map());
  /* The run's lifecycle, held apart from the batch map: `active` from beginRun to settle/reset,
   * `finalized` once finishRun has said no further batches are coming. A run may only settle
   * when it is finalized — before that, "every known batch completed" can simply mean the next
   * batch has not been submitted yet. */
  const runRef = useRef({ active: false, finalized: false });
  /* This run's module-level memory (see rememberRun) — what groups its late completions into
   * one background notification after the dialog is gone. */
  const currentRunRef = useRef(null);
  /* Events for batchIds the ACTIVE run has not registered yet — each batch's own queued event
   * outruns its mutation response by design. Drained into the batch on addBatch. */
  const bufferRef = useRef(new Map());
  const syncRef = useRef(EMPTY_SYNC);
  const timeoutRef = useRef(null);
  const onCompleteRef = useRef(null);
  const onTimeoutRef = useRef(null);
  const onBackgroundCompleteRef = useRef(onBackgroundComplete);
  const backgroundRef = useRef({ succeeded: 0, failed: 0, total: 0, timer: null });

  onBackgroundCompleteRef.current = onBackgroundComplete;

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearTimer();
    batchesRef.current = new Map();
    bufferRef.current = new Map();
    runRef.current = { active: false, finalized: false };
    syncRef.current = EMPTY_SYNC;
    onCompleteRef.current = null;
    onTimeoutRef.current = null;
    setIsTracking(false);
    setTotal(0);
    setSucceeded(0);
    setFailed(0);
    setDoneBatches(0);
    setTotalBatches(0);
    setPercent(0);
    setTimedOut(false);
  }, [clearTimer]);

  /* One walk over the batch map answers every aggregate question, so the socket handlers, the
   * timeout and the settle can never disagree about the counts. A completed batch speaks with
   * its authoritative numbers; a running one with what its chunks have reported so far. */
  const aggregates = () => {
    const sums = {
      succeeded: syncRef.current.succeeded,
      failed: syncRef.current.failed,
      total: syncRef.current.total,
      doneChunks: 0,
      totalChunks: 0,
      pending: 0,
      batchesWithSuccess: 0,
    };

    batchesRef.current.forEach((batch) => {
      const isFinal = batch.final !== null;

      sums.succeeded += isFinal ? batch.final.succeeded : batch.succeeded;
      sums.failed += isFinal ? batch.final.failed : batch.failed;
      sums.total += isFinal ? batch.final.total : batch.accepted;
      sums.totalChunks += batch.totalChunks;
      sums.doneChunks += isFinal
        ? Math.max(batch.chunksSeen.size, batch.totalChunks)
        : batch.chunksSeen.size;

      if (!isFinal) sums.pending += 1;
      if (isFinal && batch.final.succeeded > 0) sums.batchesWithSuccess += 1;
    });

    return sums;
  };

  /* In a ref (like armTimeoutRef below): read by the stable useCallbacks and by processors that
   * are themselves reached through a ref, without any of them needing it as a dependency. */
  const publishRef = useRef(null);

  publishRef.current = () => {
    const sums = aggregates();

    setSucceeded(sums.succeeded);
    setFailed(sums.failed);
    setDoneBatches(sums.doneChunks);
    setTotalBatches(sums.totalChunks);
    setTotal(sums.total);

    /* Monotonic: a later batch registering (or its queued event landing) grows the denominator,
     * and a bar that runs to 100% then jumps back to 56% reads as the run breaking. The counts
     * stay exact; only the bar refuses to move backwards. */
    const raw = sums.totalChunks > 0 ? Math.round((sums.doneChunks / sums.totalChunks) * 100) : 0;

    setPercent((current) => Math.max(current, raw));
  };

  // Arming lives in a ref so the socket handler can re-arm without the handler being rebuilt.
  const armTimeoutRef = useRef(null);

  armTimeoutRef.current = (ms) => {
    clearTimer();

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      setTimedOut(true);
      setIsTracking(false);
      runRef.current = { active: false, finalized: false };

      const handler = onTimeoutRef.current;
      const sums = aggregates();

      onTimeoutRef.current = null;
      onCompleteRef.current = null;

      handler?.({ succeeded: sums.succeeded, failed: sums.failed, total: sums.total });
    }, ms);
  };

  /* Which window the run is in: while any batch still owes chunk reports the full queued window
   * applies; once every known chunk has reported, only the authoritative completion events are
   * outstanding and the short grace covers them. */
  const rearmForRemainingWork = () => {
    const sums = aggregates();
    const allChunksReported =
      runRef.current.finalized &&
      sums.totalChunks > 0 &&
      sums.doneChunks >= sums.totalChunks &&
      [...batchesRef.current.values()].every(
        (batch) => batch.final !== null || batch.totalChunks > 0
      );

    armTimeoutRef.current(allChunksReported ? COMPLETION_GRACE_MS : queuedTimeoutMs);
  };

  const settleIfDoneRef = useRef(null);

  settleIfDoneRef.current = () => {
    if (!runRef.current.active || !runRef.current.finalized) return;

    const sums = aggregates();

    if (sums.pending > 0) return;

    clearTimer();
    runRef.current = { active: false, finalized: false };

    /* The dialog reported this run itself; the background memory must never report it again —
     * a duplicate completion event arriving after reset() would otherwise re-announce it. */
    if (currentRunRef.current) {
      currentRunRef.current.isSettled = true;

      if (currentRunRef.current.timer) clearTimeout(currentRunRef.current.timer);
    }

    setIsTracking(false);

    const handler = onCompleteRef.current;

    onCompleteRef.current = null;
    onTimeoutRef.current = null;

    handler?.({
      succeeded: sums.succeeded,
      failed: sums.failed,
      total: sums.total,
      /* How many batches actually wrote something — the label phase derives its expected
       * completion-event count from this, because core only publishes a label completion for
       * a batch whose succeeded > 0. */
      batchesWithSuccess: sums.batchesWithSuccess,
    });
  };

  /* ---------- event processing ----------
   *
   * In refs (rebuilt every render, read through the ref) so the socket effect's handlers and the
   * buffer drain in addBatch run the SAME logic — a drained event must count exactly as it would
   * have counted arriving after registration. Everything here touches only refs and setState.
   */
  const processorsRef = useRef({});

  processorsRef.current = {
    /**
     * Core accepted one batch and dispatched its chunks. Carries that batch's authoritative chunk
     * count, which the progress events alone cannot establish until the last one arrives.
     */
    queued: (eventData) => {
      const batch = batchesRef.current.get(eventData.batchId);

      // A settled batch speaks only through its final counts — a late queued event rewriting its
      // denominator would mutate displayed numbers after the run announced itself.
      if (!batch || batch.final !== null) return;

      batch.totalChunks = eventData.batches || 0;
      publishRef.current();
    },

    /**
     * One chunk of one batch finished.
     *
     * Chunk indexes are tracked in a Set per batch rather than counted, for the same reason core
     * does it that way in Redis: SQS is at-least-once, so the same chunk can report twice, and a
     * counter would double-count its successes and overshoot the total.
     */
    progress: (eventData) => {
      const batch = batchesRef.current.get(eventData.batchId);

      if (!batch || batch.final !== null) return;

      const index = eventData.batchIndex;

      if (index === undefined || batch.chunksSeen.has(index)) return;

      batch.chunksSeen.add(index);
      batch.succeeded += eventData.succeeded || 0;
      batch.failed += eventData.failed || 0;

      if (eventData.totalBatches) {
        batch.totalChunks = eventData.totalBatches;
      }

      publishRef.current();
      rearmForRemainingWork();
    },

    /**
     * One batch is done — authoritative counts for that batch. The RUN is done only when every
     * batch has said so AND finishRun has said no more are coming.
     */
    completed: (eventData) => {
      const batch = batchesRef.current.get(eventData.batchId);

      if (!batch || batch.final !== null) return;

      batch.final = {
        succeeded: eventData.succeeded ?? batch.succeeded,
        failed: eventData.failed ?? batch.failed,
        total: eventData.total ?? batch.accepted,
      };

      publishRef.current();

      if (runRef.current.finalized && aggregates().pending === 0) {
        settleIfDoneRef.current();

        return;
      }

      /* Not terminal while siblings are still running or still being submitted — and the timer
       * MUST be re-armed, not just left alone: clearing it here (the single-batch behaviour)
       * would remove the only backstop for a sibling whose completion event never arrives. */
      rearmForRemainingWork();
    },
  };

  const bufferEvent = (type, eventData) => {
    const buffer = bufferRef.current;
    const { batchId } = eventData;

    if (!buffer.has(batchId)) {
      if (buffer.size >= MAX_BUFFERED_BATCHES) return;

      buffer.set(batchId, []);
    }

    const events = buffer.get(batchId);

    if (events.length >= MAX_BUFFERED_EVENTS_PER_BATCH) return;

    events.push({ type, eventData });
  };

  /**
   * Open a run before its first mutation is on the wire. From here until settle, events for
   * batchIds the run has not registered yet are buffered rather than dropped or backgrounded —
   * core publishes a batch's queued event before that batch's own mutation resolves, so they
   * WILL arrive early.
   */
  const beginRun = useCallback(
    ({ onComplete, onTimeout }) => {
      clearTimer();

      batchesRef.current = new Map();
      bufferRef.current = new Map();
      runRef.current = { active: true, finalized: false };
      currentRunRef.current = rememberRun();
      syncRef.current = EMPTY_SYNC;
      onCompleteRef.current = onComplete;
      onTimeoutRef.current = onTimeout;

      setIsTracking(true);
      setTotal(0);
      setSucceeded(0);
      setFailed(0);
      setDoneBatches(0);
      setTotalBatches(0);
      setPercent(0);
      setTimedOut(false);

      armTimeoutRef.current(queuedTimeoutMs);
    },
    [clearTimer, queuedTimeoutMs]
  );

  /** Register one accepted batch and replay anything it said before it was registered. */
  const addBatch = useCallback(({ batchId, accepted }) => {
    if (!runRef.current.active || !batchId || batchesRef.current.has(batchId)) return;

    const batch = {
      accepted: accepted || 0,
      chunksSeen: new Set(),
      succeeded: 0,
      failed: 0,
      totalChunks: 0,
      final: null,
    };

    batchesRef.current.set(batchId, batch);

    const rememberedRun = currentRunRef.current;

    if (rememberedRun) {
      rememberedRun.batchIds.add(batchId);
      runsByBatchId.set(batchId, rememberedRun);
    }

    const buffered = bufferRef.current.get(batchId);

    bufferRef.current.delete(batchId);
    buffered?.forEach(({ type, eventData }) => processorsRef.current[type](eventData));

    publishRef.current();
  }, []);

  /**
   * No further batches are coming. `sync` carries the counts of any chunks the server ran
   * synchronously instead of queueing (the small remainder chunk, or every chunk when the async
   * kill switch is off); they fold into the totals as already-finished work. If every batch has
   * already completed — buffered events included — this settles the run.
   */
  const finishRun = useCallback(({ sync = EMPTY_SYNC } = {}) => {
    if (!runRef.current.active) return;

    runRef.current.finalized = true;

    if (currentRunRef.current) currentRunRef.current.isFinalized = true;

    syncRef.current = { ...EMPTY_SYNC, ...sync };
    publishRef.current();
    settleIfDoneRef.current();
  }, []);

  /** The single-mutation path: one batch, known in full the moment the mutation resolves. */
  const startTracking = useCallback(
    ({ batches, sync = EMPTY_SYNC, onComplete, onTimeout }) => {
      beginRun({ onComplete, onTimeout });
      batches.forEach(addBatch);
      finishRun({ sync });
    },
    [beginRun, addBatch, finishRun]
  );

  useEffect(() => {
    if (!socket || !isConnected) {
      return undefined;
    }

    // The relay has been seen to deliver either the object or a single-element array, and core
    // nests its own payload under `message` — same normalisation as useLabelRegenerationProgress.
    const readEvent = (payload) => {
      const data = Array.isArray(payload) ? payload[0] : payload;

      return data?.message || data;
    };

    /* Route one event: to its registered batch; into the buffer while a run is active but has
     * not registered this batchId yet; otherwise nowhere (queued/progress) or to the background
     * accumulator (completed — handled by its caller). Returns true when consumed. */
    const routeToRun = (type, eventData) => {
      if (!eventData?.batchId) return true;

      if (batchesRef.current.has(eventData.batchId)) {
        processorsRef.current[type](eventData);

        return true;
      }

      if (runRef.current.active) {
        bufferEvent(type, eventData);

        return true;
      }

      return false;
    };

    const handleQueued = (payload) => {
      routeToRun('queued', readEvent(payload));
    };

    const handleProgress = (payload) => {
      routeToRun('progress', readEvent(payload));
    };

    /**
     * Scoping is server-side too — completions are delivered only to the initiating user's
     * personal room — but we still match on batchId so a second run in another tab cannot settle
     * this one. A completion no run claims is a background notification: this tab refreshed, or
     * the run was started elsewhere. Summed with a quiet gap, because a chunked run arrives as
     * several of these.
     */
    const handleCompleted = (payload) => {
      const eventData = readEvent(payload);

      if (routeToRun('completed', eventData)) return;

      /* A run this tab STARTED but no longer tracks — the dialog closed on a timeout, the
       * operator closed it, or the screen unmounted. Its batches finish minutes apart behind a
       * stalled queue, so they are grouped by remembered membership, not by a debounce: ONE
       * notification when the whole run has reported, or after a quiet minute for a straggler
       * that never will. A run the dialog already reported is never re-announced. */
      const rememberedRun = eventData?.batchId ? runsByBatchId.get(eventData.batchId) : undefined;

      if (rememberedRun) {
        if (rememberedRun.isSettled || rememberedRun.received.has(eventData.batchId)) return;

        rememberedRun.received.add(eventData.batchId);
        rememberedRun.totals.total += eventData?.total ?? 0;
        rememberedRun.totals.succeeded += eventData?.succeeded ?? 0;
        rememberedRun.totals.failed += eventData?.failed ?? 0;

        if (rememberedRun.timer) clearTimeout(rememberedRun.timer);

        const isRunComplete =
          rememberedRun.isFinalized && rememberedRun.received.size >= rememberedRun.batchIds.size;

        if (isRunComplete) {
          rememberedRun.isSettled = true;
          onBackgroundCompleteRef.current?.({ ...rememberedRun.totals });

          return;
        }

        rememberedRun.timer = setTimeout(() => {
          rememberedRun.timer = null;
          rememberedRun.isSettled = true;
          onBackgroundCompleteRef.current?.({ ...rememberedRun.totals });
        }, BACKGROUND_RUN_QUIET_MS);

        return;
      }

      const background = backgroundRef.current;

      background.total += eventData?.total ?? 0;
      background.succeeded += eventData?.succeeded ?? 0;
      background.failed += eventData?.failed ?? 0;

      if (background.timer) clearTimeout(background.timer);
      background.timer = setTimeout(() => {
        const { total: bgTotal, succeeded: bgSucceeded, failed: bgFailed } = background;

        backgroundRef.current = { succeeded: 0, failed: 0, total: 0, timer: null };

        /* A run that became active since these accumulated owns the dialog now; announcing a
         * "finished" total underneath its live progress bar would be read as THAT run. Dropped,
         * not deferred — by settle time the numbers would be stale and unattributable. */
        if (runRef.current.active) return;

        onBackgroundCompleteRef.current?.({
          total: bgTotal,
          succeeded: bgSucceeded,
          failed: bgFailed,
        });
      }, BACKGROUND_AGGREGATE_MS);
    };

    socket.on('ndc_override_queued', handleQueued);
    socket.on('ndc_override_progress', handleProgress);
    socket.on('ndc_override_completed', handleCompleted);

    return () => {
      socket.off('ndc_override_queued', handleQueued);
      socket.off('ndc_override_progress', handleProgress);
      socket.off('ndc_override_completed', handleCompleted);
    };
    /* processorsRef/bufferEvent/routeToRun are ref-plumbing rebuilt each render; the one real
     * dependency this closure omits is nothing — queuedTimeoutMs is reached only through
     * armTimeoutRef, which is reassigned every render. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, isConnected, clearTimer]);

  // Never leave a timer behind on unmount — the run timeout or a pending background aggregate.
  useEffect(
    () => () => {
      clearTimer();

      if (backgroundRef.current.timer) clearTimeout(backgroundRef.current.timer);
    },
    [clearTimer]
  );

  return useMemo(
    () => ({
      beginRun,
      addBatch,
      finishRun,
      startTracking,
      reset,
      isTracking,
      timedOut,
      total,
      succeeded,
      failed,
      doneBatches,
      totalBatches,
      processedCount: succeeded + failed,
      /**
       * Chunk-based, not item-based: progress is only ever reported per chunk, so deriving the
       * percentage from processed items would jump in large steps and could read as stalled
       * between them. 0 until the first chunk count is known, so the caller can render an
       * indeterminate bar; monotonic after that (see publish).
       */
      percent,
      isRealtimeAvailable: Boolean(socket && isConnected),
    }),
    [
      beginRun,
      addBatch,
      finishRun,
      startTracking,
      reset,
      isTracking,
      timedOut,
      total,
      succeeded,
      failed,
      doneBatches,
      totalBatches,
      percent,
      socket,
      isConnected,
    ]
  );
};

export default useNdcOverrideProgress;
